import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { IbhShape } from '@igus/iglidur-bearing-hub-api';
import type { Session } from '@hub/hooks/states/useSessionStore';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { Article } from '@hub/bearing-hub/ProductDetails/ArticleData/article';
import {
  type IbhCatalogArticleResponseDto,
  type IbhCustomArticleResponseDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import getConfig from 'next/config';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import { useHasDynamicPrice } from '@hub/hooks/useHasDynamicPrice';
import type { UnitSystem } from '@igus/kopla-domain';

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

interface UseArticleProps {
  articleNumber: string;
  isCustom: boolean;
  quantity: number;
  shapeKey: IbhShape;
  dimensions?: IbhDimensions;
  unitSystem: UnitSystem;
  session: Session;
  initialArticleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto;
}

export const useArticle = ({
  articleNumber,
  isCustom,
  quantity,
  shapeKey,
  session,
  unitSystem,
  dimensions,
  initialArticleData,
}: UseArticleProps) => {
  const queryKey = [
    'article',
    articleNumber,
    isCustom,
    quantity,
    shapeKey,
    session?.currency,
    unitSystem,
    JSON.stringify(dimensions),
  ];

  const fetchArticleData = async () => {
    const article = Article({
      articleNumber,
      isCustom,
      quantity,
      shapeKey,
      session,
      unitSystem,
      dimensions,
      productionMethod: initialArticleData.productionMethod,
    });
    return article.fetchArticleData();
  };
  const hasDynamicPrices = useHasDynamicPrice();
  const enabled =
    (!!session && quantity !== initialArticleData.quantity) ||
    (!!session && typeof window !== 'undefined' && hasDynamicPrices);

  const {
    data: articleData,
    isFetching,
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: fetchArticleData,
    enabled,
    gcTime: publicRuntimeConfig.ibhUseQueryCacheTtl,
    placeholderData: initialArticleData,
    retry: failureCount => failureCount <= 1 && navigator.onLine,
  }) as UseQueryResult<IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto>;

  return {
    article: articleData || initialArticleData,
    articleIsLoading: isLoading || isFetching,
    error,
  };
};
